/**
 * Insert / remove time and markers (W2-03 note-lane; WORKPACKAGES Appendix B `time.insert|remove`, `markers.*`;
 * DESIGN.md §4.6 "Insert / remove time", §8.2 Navigation; RPE timeTags).
 *
 *  - "Insert beats at playhead..." (time gutter context menu, Song & Timing tempo panel context menu, palette with a
 *    beat argument): asks the length and the scope (the whole chart, the active line or chosen lines), then runs
 *    `Core.transform.insertTime` at the snapped playhead as one undoable step and reports what moved ("Inserted 2
 *    beats: moved 1,204 notes, 312 keyframes, 2 tempo points").
 *  - "Remove selected time range..." takes the time range dragged in the gutter (TimelineHost `getTimeRange`), asks
 *    the scope and runs `Core.transform.removeTime`, reporting deleted and moved items.
 *  - Markers: M adds one at the playhead (or at the right-clicked gutter beat), Ctrl+Left / Ctrl+Right (macOS:
 *    Alt+Shift+Left / Right) jump to the previous / next one, the gutter context menu renames and deletes the marker
 *    under the click (else the selected one, else the one at the playhead). Markers show in the gutter and the
 *    overview strip through this module's decoration provider; clicking one seeks there and selects it.
 * A scope of chosen lines that includes a locked line is refused (lock only blocks editing); the whole-chart scope
 * moves every line, locked ones included, so the chart's timing stays consistent.
 */

import { Beats } from '../../core/time/beat';
import type { Beat } from '../../core/time/types';
import type { MessageRef, Unsubscribe } from '../../core/util/types';
import type { LineId, Marker, MarkerId, TrackOwner } from '../../core/model/types';
import { Core } from '../../core/commands/index';
import type { AppServices, CommandContext, CommandDef, KeyBinding, KeymapPresetId } from '../../app/registry/types';
import type { TimelineDecoration, TimelineDecorationProvider } from '../../render/types';
import { timeRangeF64 } from '../../render/timeline/TimelineHost';
import { m } from './noteLane.messages';
import {
  activeLineOf,
  beatCountText,
  beatText,
  hostOf,
  laneSession,
  lockedReason,
  playheadBeat,
  ref,
  seekToBeat,
  snapBeatAt,
  type InsertTimeChoice,
  type LaneDialog,
  type TimeScope,
} from './laneSession';

type Enabled = true | MessageRef;

function projectOpen(ctx: CommandContext): Enabled {
  return ctx.store !== null ? true : ref(m.reasonNoProject);
}

function obj(args: unknown): Readonly<Record<string, unknown>> {
  return typeof args === 'object' && args !== null && !Array.isArray(args) ? (args as Record<string, unknown>) : {};
}

function beatFromArg(v: unknown): Beat | null {
  const o = obj(v);
  const { w, n, d } = o;
  if (typeof w !== 'number' || typeof n !== 'number' || typeof d !== 'number') return null;
  try {
    return Beats.make(w, n, d);
  } catch {
    return null;
  }
}

/** Opens a package dialog (rendered by LaneShellHost in every editor workspace) and resolves with its answer. */
export function askDialog<R>(
  services: AppServices,
  make: (resolve: (r: R | null) => void) => LaneDialog,
): Promise<R | null> {
  const s = laneSession(services);
  return new Promise<R | null>((resolve) => {
    // A dialog already open is answered with "cancel" first.
    const prev = s.dialog;
    if (prev !== null) prev.resolve(null);
    s.dialog = make((r) => {
      if (s.dialog !== null) s.dialog = null;
      s.emit();
      resolve(r);
    });
    s.emit();
  });
}

// ---------------------------------------------------------------------------------------------------------------
// Insert / remove time
// ---------------------------------------------------------------------------------------------------------------

function inScope(scope: TimeScope, line: LineId): boolean {
  return scope === 'all' || scope.includes(line);
}

function ownerLine(owner: TrackOwner): LineId | null {
  return owner.kind === 'line' ? owner.line : null;
}

/** What insertTime will move (counted before the transaction for the report toast). */
export function countInsert(
  services: AppServices,
  at: Beat,
  scope: TimeScope,
): { notes: number; keys: number; tempo: number } {
  const store = services.store;
  if (store === null) return { notes: 0, keys: 0, tempo: 0 };
  const doc = store.doc;
  let notes = 0;
  for (const n of doc.notes.values()) if (inScope(scope, n.line) && !Beats.lt(n.t, at)) notes++;
  let keys = 0;
  for (const k of doc.keys.values()) {
    const tr = doc.tracks.get(k.track);
    if (tr === undefined) continue;
    const line = ownerLine(tr.owner);
    if (line === null ? scope !== 'all' : !inScope(scope, line)) continue;
    if (!Beats.lt(k.t, at)) keys++;
  }
  let tempo = 0;
  if (scope === 'all')
    for (const p of doc.bpm.values())
      if (Beats.lt(at, p.t) || (Beats.eq(p.t, at) && !Beats.eq(at, Beats.ZERO))) tempo++;
  return { notes, keys, tempo };
}

/** What removeTime will delete and move. */
export function countRemove(
  services: AppServices,
  from: Beat,
  to: Beat,
  scope: TimeScope,
): { deleted: number; notes: number; keys: number; tempo: number } {
  const store = services.store;
  if (store === null) return { deleted: 0, notes: 0, keys: 0, tempo: 0 };
  const doc = store.doc;
  let deleted = 0;
  let notes = 0;
  for (const n of doc.notes.values()) {
    if (!inScope(scope, n.line)) continue;
    if (!Beats.lt(n.t, from) && Beats.lt(n.t, to)) deleted++;
    else if (!Beats.lt(n.t, to)) notes++;
  }
  let keys = 0;
  for (const k of doc.keys.values()) {
    const tr = doc.tracks.get(k.track);
    if (tr === undefined) continue;
    const line = ownerLine(tr.owner);
    if (line === null ? scope !== 'all' : !inScope(scope, line)) continue;
    if (!Beats.lt(k.t, to)) keys++;
  }
  let tempo = 0;
  if (scope === 'all') for (const p of doc.bpm.values()) if (!Beats.lt(p.t, to)) tempo++;
  return { deleted, notes, keys, tempo };
}

/** The insert point: the playhead snapped to the grid in effect. */
export function insertPoint(services: AppServices): Beat {
  return snapBeatAt(playheadBeat(services), services.editor.getState().snap, false);
}

/** A scope of chosen lines with a locked one: toasts why and returns true. */
function refuseLockedScope(services: AppServices, scope: TimeScope): boolean {
  if (scope === 'all') return false;
  const locked = lockedReason(services, { lines: scope });
  if (locked === null) return false;
  services.ui.toast(locked, { kind: 'warning' });
  return true;
}

/** Runs insertTime and reports; true when the chart changed. */
export function insertTime(services: AppServices, at: Beat, length: Beat, scope: TimeScope): boolean {
  const store = services.store;
  if (store === null) return false;
  if (!Beats.lt(Beats.ZERO, length)) {
    services.ui.toast(ref(m.invalidLength), { kind: 'warning' });
    return false;
  }
  if (refuseLockedScope(services, scope)) return false;
  const counts = countInsert(services, at, scope);
  const { text: beats, one } = beatCountText(length);
  const out = store.transact(
    { label: ref(m.txInsertTime, { beats, one }), command: 'time.insert', focus: { beat: at } },
    (tx) => Core.transform.insertTime(tx, { at, length, scope }),
  );
  if (!out.ok) {
    services.ui.toast(out.reason, { kind: 'error' });
    return false;
  }
  services.ui.toast(ref(m.toastInserted, { beats, one, ...counts }), { kind: 'success', undo: true, durationMs: 6000 });
  for (const n of out.notes) services.ui.toast(n, { kind: 'info' });
  return true;
}

/** Runs removeTime over [from, to) and reports. */
export function removeTime(services: AppServices, from: Beat, to: Beat, scope: TimeScope): boolean {
  const store = services.store;
  if (store === null || !Beats.lt(from, to)) return false;
  if (refuseLockedScope(services, scope)) return false;
  const counts = countRemove(services, from, to, scope);
  const { text: beats, one } = beatCountText(Beats.sub(to, from));
  const out = store.transact(
    { label: ref(m.txRemoveTime, { beats, one }), command: 'time.remove', focus: { beat: from } },
    (tx) => Core.transform.removeTime(tx, { from, to, scope }),
  );
  if (!out.ok) {
    services.ui.toast(out.reason, { kind: 'error' });
    return false;
  }
  services.ui.toast(ref(m.toastRemoved, { beats, one, ...counts }), { kind: 'success', undo: true, durationMs: 6000 });
  for (const n of out.notes) services.ui.toast(n, { kind: 'info' });
  hostOf(laneSession(services))?.setTimeRange(null);
  return true;
}

/** The gutter's time range as exact beats (null when none or empty). */
export function selectedTimeRange(services: AppServices): { readonly from: Beat; readonly to: Beat } | null {
  const host = hostOf(laneSession(services));
  const r = host?.getTimeRange() ?? null;
  if (r === null) return null;
  const [a, b] = timeRangeF64(r);
  if (!(b > a)) return null;
  return { from: r.start, to: r.end };
}

const timeCommands: readonly CommandDef[] = [
  {
    id: 'time.insert',
    title: m.timeInsert,
    category: 'Timing',
    icon: 'BetweenHorizontalStart',
    aliases: ['insert time', 'insert beats', 'shift everything', 'make room'],
    level: 'guided',
    args: { kind: 'beat', label: ref(m.argBeats) },
    enabled: projectOpen,
    menus: [
      { menu: 'timing.context', group: 'time', order: 10 },
      { menu: 'lane.context', group: 'time', order: 10 },
    ],
    run: async (ctx, args) => {
      const at = insertPoint(ctx);
      const given = beatFromArg(args) ?? beatFromArg(obj(args).beats);
      if (given !== null) {
        insertTime(ctx, at, given, 'all');
        return;
      }
      const choice = await askDialog<InsertTimeChoice>(ctx, (resolve) => ({ kind: 'insertTime', at, resolve }));
      if (choice !== null) insertTime(ctx, at, choice.length, choice.scope);
    },
  },
  {
    id: 'time.remove',
    title: m.timeRemove,
    category: 'Timing',
    icon: 'BetweenHorizontalEnd',
    aliases: ['remove time', 'delete time range', 'cut time'],
    level: 'guided',
    enabled: (ctx) => {
      if (ctx.store === null) return ref(m.reasonNoProject);
      return selectedTimeRange(ctx) === null ? ref(m.reasonNoTimeRange) : true;
    },
    menus: [
      { menu: 'timing.context', group: 'time', order: 20 },
      { menu: 'lane.context', group: 'time', order: 20 },
    ],
    run: async (ctx) => {
      const range = selectedTimeRange(ctx);
      if (range === null) return;
      const choice = await askDialog<{ readonly scope: TimeScope }>(ctx, (resolve) => ({
        kind: 'removeTime',
        from: range.from,
        to: range.to,
        resolve,
      }));
      if (choice !== null) removeTime(ctx, range.from, range.to, choice.scope);
    },
  },
];

// ---------------------------------------------------------------------------------------------------------------
// Markers
// ---------------------------------------------------------------------------------------------------------------

/** Markers sorted by beat. */
export function sortedMarkers(services: AppServices): Marker[] {
  const store = services.store;
  if (store === null) return [];
  return [...store.doc.markers.values()].sort((a, b) => Beats.cmp(a.t, b.t) || a.id - b.id);
}

/**
 * Marker a command acts on: explicit argument, the right-clicked gutter marker (commands chosen from a menu), the
 * selection, the marker at the playhead.
 */
export function targetMarker(services: AppServices, args: unknown, fromMenu = true): MarkerId | null {
  const store = services.store;
  if (store === null) return null;
  const v = typeof args === 'number' ? args : obj(args).marker;
  if (typeof v === 'number' && store.doc.markers.has(v as MarkerId)) return v as MarkerId;
  const s = laneSession(services);
  const g = fromMenu ? (s.gutter?.marker ?? null) : null;
  if (g !== null && store.doc.markers.has(g)) return g;
  const sel = [...services.editor.getState().selection.markers].find((id) => store.doc.markers.has(id));
  if (sel !== undefined) return sel;
  const ph = playheadBeat(services);
  const near = sortedMarkers(services).find((mk) => Math.abs(Beats.toF64(mk.t) - ph) < 1e-6);
  return near?.id ?? null;
}

/** Adds a marker at a beat with the next default name; returns its id. */
export function addMarker(services: AppServices, at: Beat): MarkerId | null {
  const store = services.store;
  if (store === null) return null;
  const names = new Set([...store.doc.markers.values()].map((mk) => mk.name));
  let n = store.doc.markers.size + 1;
  let name = services.i18n.t(m.markerDefaultName, { n });
  while (names.has(name)) name = services.i18n.t(m.markerDefaultName, { n: ++n });
  const out = store.transact({ label: ref(m.txMarkerAdd, { name }), command: 'markers.add' }, (tx) =>
    Core.tempo.addMarker(tx, { t: at, name }),
  );
  if (!out.ok) {
    services.ui.toast(out.reason, { kind: 'error' });
    return null;
  }
  services.ui.toast(ref(m.toastMarkerAdded, { name, beat: beatText(services, at) }), {
    kind: 'success',
    durationMs: 5000,
    action: { label: ref(m.actionRename), command: 'markers.rename', args: { marker: out.value } },
  });
  return out.value;
}

/** Jumps to the previous / next marker from the playhead (toast when there is none). */
export function jumpMarker(services: AppServices, dir: 1 | -1): boolean {
  const ph = playheadBeat(services);
  const list = sortedMarkers(services).map((mk) => Beats.toF64(mk.t));
  const eps = 1e-6;
  const target = dir === 1 ? list.find((b) => b > ph + eps) : [...list].reverse().find((b) => b < ph - eps);
  if (target === undefined) {
    services.ui.toast(ref(dir === 1 ? m.toastNoNextMarker : m.toastNoPrevMarker), { kind: 'info', durationMs: 2500 });
    return false;
  }
  seekToBeat(services, target);
  return true;
}

export async function renameMarker(services: AppServices, id: MarkerId): Promise<boolean> {
  const store = services.store;
  const mk = store?.doc.markers.get(id);
  if (store === null || mk === undefined) return false;
  const name = await askDialog<string>(services, (resolve) => ({
    kind: 'renameMarker',
    marker: id,
    name: mk.name,
    resolve,
  }));
  if (name === null || name.trim() === '' || name.trim() === mk.name) return false;
  const out = store.transact(
    { label: ref(m.txMarkerRename, { name: name.trim() }), command: 'markers.rename' },
    (tx) => {
      Core.tempo.patchMarker(tx, { id, name });
    },
  );
  if (!out.ok) services.ui.toast(out.reason, { kind: 'error' });
  return out.ok;
}

export function deleteMarker(services: AppServices, id: MarkerId): boolean {
  const store = services.store;
  const mk = store?.doc.markers.get(id);
  if (store === null || mk === undefined) return false;
  const out = store.transact({ label: ref(m.txMarkerDelete, { name: mk.name }), command: 'markers.delete' }, (tx) => {
    Core.tempo.deleteMarker(tx, { id });
  });
  if (!out.ok) services.ui.toast(out.reason, { kind: 'error' });
  else services.ui.toast(ref(m.txMarkerDelete, { name: mk.name }), { kind: 'info', undo: true });
  return out.ok;
}

function markersExist(ctx: CommandContext): Enabled {
  if (ctx.store === null) return ref(m.reasonNoProject);
  return ctx.store.doc.markers.size > 0 ? true : ref(m.reasonNoMarkers);
}

function markerTargetEnabled(ctx: CommandContext): Enabled {
  const base = markersExist(ctx);
  if (base !== true) return base;
  return targetMarker(ctx, undefined) === null ? ref(m.reasonNoMarker) : true;
}

const markerCommands: readonly CommandDef[] = [
  {
    id: 'markers.add',
    title: m.markerAdd,
    category: 'View',
    icon: 'BookmarkPlus',
    aliases: ['marker', 'bookmark', 'time tag', 'timeTags'],
    level: 'guided',
    hint: 2,
    enabled: projectOpen,
    menus: [{ menu: 'timing.context', group: 'markers', order: 10 }],
    run: (ctx) => {
      const s = laneSession(ctx);
      const fromGutter = ctx.source === 'context' && s.gutter !== null;
      const beat = fromGutter && s.gutter !== null ? s.gutter.beat : playheadBeat(ctx);
      addMarker(ctx, snapBeatAt(beat, ctx.editor.getState().snap, false));
    },
  },
  {
    id: 'markers.prev',
    title: m.markerPrev,
    category: 'View',
    icon: 'ChevronsDown',
    aliases: ['previous marker', 'jump marker'],
    level: 'guided',
    repeat: true,
    enabled: markersExist,
    run: (ctx) => {
      jumpMarker(ctx, -1);
    },
  },
  {
    id: 'markers.next',
    title: m.markerNext,
    category: 'View',
    icon: 'ChevronsUp',
    aliases: ['next marker', 'jump marker'],
    level: 'guided',
    repeat: true,
    enabled: markersExist,
    run: (ctx) => {
      jumpMarker(ctx, 1);
    },
  },
  {
    id: 'markers.rename',
    title: m.markerRename,
    category: 'View',
    icon: 'Pencil',
    aliases: ['rename marker'],
    level: 'guided',
    enabled: markerTargetEnabled,
    menus: [{ menu: 'timing.context', group: 'markers', order: 20 }],
    run: async (ctx, args) => {
      const id = targetMarker(ctx, args, ctx.source === 'context' || ctx.source === 'menu');
      if (id !== null) await renameMarker(ctx, id);
    },
  },
  {
    id: 'markers.delete',
    title: m.markerDelete,
    category: 'View',
    icon: 'BookmarkX',
    aliases: ['delete marker', 'remove marker'],
    level: 'guided',
    enabled: markerTargetEnabled,
    menus: [{ menu: 'timing.context', group: 'markers', order: 30 }],
    run: (ctx, args) => {
      const id = targetMarker(ctx, args, ctx.source === 'context' || ctx.source === 'menu');
      if (id !== null) deleteMarker(ctx, id);
    },
  },
];

export const TIME_COMMANDS: readonly CommandDef[] = [...timeCommands, ...markerCommands];

const kb = (chord: string, command: string, extra: Partial<KeyBinding> = {}): KeyBinding => ({
  chord,
  command,
  ...extra,
});

const SHARED: readonly KeyBinding[] = [
  kb('KeyM', 'markers.add'),
  // Cmd+Left / Cmd+Right are history navigation on macOS: Alt+Shift+Left / Right there (DESIGN.md §8.1).
  kb('Ctrl+ArrowLeft', 'markers.prev', { mac: 'Alt+Shift+ArrowLeft' }),
  kb('Ctrl+ArrowRight', 'markers.next', { mac: 'Alt+Shift+ArrowRight' }),
];

export const TIME_KEYBINDINGS: Readonly<Record<KeymapPresetId, readonly KeyBinding[]>> = {
  default: SHARED,
  rpe: SHARED,
};

// ---------------------------------------------------------------------------------------------------------------
// Marker decorations (gutter and overview strip)
// ---------------------------------------------------------------------------------------------------------------

/** Markers as timeline decorations; clicking one seeks there and selects it. */
export function markerDecorations(services: AppServices): TimelineDecorationProvider {
  let cache: { readonly v: number; readonly store: object; readonly list: readonly TimelineDecoration[] } | null = null;
  const build = (): readonly TimelineDecoration[] => {
    const store = services.store;
    if (store === null) return [];
    const v = store.version('markers');
    if (cache?.store === store && cache.v === v) return cache.list;
    const list = sortedMarkers(services).map((mk): TimelineDecoration => ({
      beat: Beats.toF64(mk.t),
      kind: 'marker',
      label: mk.name,
      onClick: () => {
        seekToBeat(services, Beats.toF64(mk.t));
        services.editor
          .getState()
          .select({ markers: new Set([mk.id]), primary: { kind: 'marker', id: mk.id } }, 'replace');
      },
    }));
    cache = { v, store, list };
    return list;
  };
  return {
    id: 'note-lane.markers',
    decorations(range) {
      const [b0, b1] = range;
      return build().filter((d) => d.beat >= b0 && d.beat <= b1);
    },
    subscribe(cb): Unsubscribe {
      let offStore: Unsubscribe | null = null;
      const attach = (): void => {
        offStore?.();
        offStore = null;
        const store = services.store;
        if (store === null) return;
        let last = store.version('markers');
        offStore = store.subscribe(() => {
          const v = store.version('markers');
          if (v !== last) {
            last = v;
            cb();
          }
        });
      };
      attach();
      const offChange = services.onChange(() => {
        attach();
        cb();
      });
      return () => {
        offStore?.();
        offChange();
      };
    },
  };
}

/** Marker under a gutter beat (within `tolPx` at the current zoom), for the gutter context menu. */
export function markerNear(services: AppServices, beat: number, pxPerBeat: number, tolPx = 6): MarkerId | null {
  let best: MarkerId | null = null;
  let bestD = Number.POSITIVE_INFINITY;
  for (const mk of sortedMarkers(services)) {
    const d = Math.abs(Beats.toF64(mk.t) - beat) * pxPerBeat;
    if (d <= tolPx && d < bestD) {
      best = mk.id;
      bestD = d;
    }
  }
  return best;
}

/** Scope options of the time dialogs. */
export function scopeOptions(services: AppServices): { readonly active: LineId | null } {
  return { active: activeLineOf(services) };
}
